import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClipboardDocumentIcon,
  UserIcon,
  CheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../../assets/logo-nanthiat.png";

const iconByType = {
  Appel: <PhoneIcon className="w-5 h-5 text-blue-500" />,
  Email: <EnvelopeIcon className="w-5 h-5 text-yellow-500" />,
  "Rendez-vous": <CalendarIcon className="w-5 h-5 text-green-500" />,
  "Message LinkedIn": <EnvelopeIcon className="w-5 h-5 text-indigo-500" />,
  "Note interne": <ClipboardDocumentIcon className="w-5 h-5 text-gray-500" />,
  Autre: <UserIcon className="w-5 h-5 text-gray-400" />,
};

const suggestionsParStatut = {
  Lead: [
    { type: "Appel", note: "Premier échange téléphonique" },
  ],
  Prospect: [
    { type: "Email", note: "Envoyer brochure de présentation" },
    { type: "Message LinkedIn", note: "Ajouter sur LinkedIn" },
    { type: "Rendez-vous", note: "Proposer un rendez-vous" },
    { type: "Note interne", note: "Préparer la Lettre d’Entrée en Relation" },
    { type: "Email", note: "Faire signer la Lettre d’Entrée en Relation" },
  ],
  "Prospect Qualifié": [
    { type: "Email", note: "Envoyer le questionnaire patrimonial" },
    { type: "Note interne", note: "Collecter documents financiers et administratifs" },
    { type: "Rendez-vous", note: "Fixer un rendez-vous pour l'audit patrimonial" },
  ],
  Client: [
    { type: "Note interne", note: "Lancer l’analyse du patrimoine" },
    { type: "Email", note: "Envoyer proposition d’allocation" },
    { type: "Appel", note: "Relancer pour retour sur proposition" },
  ],
  "Sans Suite": [
    { type: "Note interne", note: "Clore le dossier (sans suite)" },
  ],
};

export default function ProspectStatus({ prospect, onStatutUpdate }) {
  const [prochainesActions, setProchainesActions] = useState([]);
  const [showAuditButton, setShowAuditButton] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const navigate = useNavigate();

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  useEffect(() => {
    fetchNextSteps();
  }, [prospect]);
  
  const fetchNextSteps = async () => {
    if (!prospect?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/interactions/${prospect._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      const allInteractions = res.data;
  
      const futures = allInteractions
        .filter((i) => i.prochaineEtape || i.dateProchaineInteraction)
        .sort((a, b) => new Date(a.dateProchaineInteraction) - new Date(b.dateProchaineInteraction));
  
      const suggestionsDéjàRéalisées = new Set(
        allInteractions.map((i) => `${i.typeInteraction}_${i.contenu?.trim()}`)
      );
  
      const suggestionsBrutes = suggestionsParStatut[prospect?.statut || "Prospect"] || [];
      const suggestionsFiltrées = suggestionsBrutes.filter((sugg) => {
        const clé = `${sugg.type}_${sugg.note.trim()}`;
        return !suggestionsDéjàRéalisées.has(clé);
      });
  
      const actionsFutures = futures.map((item) => ({
        icon: iconByType[item.typeInteraction],
        titre: item.prochaineEtape || "Étape à préciser",
        date: item.dateProchaineInteraction
          ? new Date(item.dateProchaineInteraction).toLocaleDateString()
          : null,
        canal: item.canal,
        type: item.typeInteraction,
      }));
  
      const actionsSuggestions = suggestionsFiltrées.map((sugg) => ({
        icon: iconByType[sugg.type],
        titre: sugg.note,
        type: sugg.type,
        canal: "N/A",
        date: null,
      }));
  
      setProchainesActions([...actionsFutures, ...actionsSuggestions]);

      const rdvAuditEstFixe = allInteractions.some(
        (i) =>
          i.typeInteraction === "Rendez-vous" &&
          i.contenu === "Fixer un rendez-vous pour l'audit patrimonial"
      );
      setShowAuditButton(rdvAuditEstFixe);
    } catch (err) {
      console.error("Erreur récupération prochaines étapes :", err);
      setProchainesActions([]);
    }
  };  

  const handleMarkAsDone = async (action) => {
    try {
      const token = localStorage.getItem("token");
  
      // 1. Créer l'interaction
      await axios.post(
        `${BACKEND_URL}/interactions`,
        {
          typeInteraction: action.type,
          canal: action.canal === "N/A" ? "Autre" : action.canal,
          date: new Date(),
          contenu: action.titre,
          statut: prospect.statut,
          referent: prospect.referent || "Non assigné",
          prospectId: prospect._id,
          commentaireInterne: "Interaction ajoutée depuis la section 'À Faire'.",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      // 2. Signature LER → Prospect Qualifié
      if (
        prospect.statut === "Prospect" &&
        action.titre === "Faire signer la Lettre d’Entrée en Relation"
      ) {
        await axios.put(
          `${BACKEND_URL}/prospects/${prospect._id}`,
          { statut: "Prospect Qualifié" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
  
        if (onStatutUpdate) onStatutUpdate("Prospect Qualifié");
  
        await fetchNextSteps();
        showToast("Interaction enregistrée et statut mis à jour vers 'Prospect Qualifié' !");
        return;
      }
  
      // 3. RDV Audit → Afficher bouton Audit
      if (
        prospect.statut === "Prospect Qualifié" &&
        action.titre === "Fixer un rendez-vous pour l'audit patrimonial"
      ) {
        setShowAuditButton(true);
        await fetchNextSteps();
        showToast("Interaction enregistrée ! Vous pouvez maintenant lancer l'audit patrimonial.");
        return;
      }
  
      // 4. Cas général
      await fetchNextSteps();
      showToast("Interaction enregistrée !");
    } catch (err) {
      console.error("Erreur enregistrement interaction ou mise à jour statut :", err);
    }
  };      

  const handleConfirmAudit = async () => {
    try {
      const token = localStorage.getItem("token");
  
      // Appelle la route backend qui gère tout : transfert + suppression
      const res = await axios.post(
        `${BACKEND_URL}/prospects/transformer/${prospect._id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      // Affiche confirmation + redirection dynamique si besoin
      showToast(res.data.message || "Prospect transformé avec succès", "success");
      setShowConfirmModal(false);
      setShowFinalModal(true);
  
    } catch (error) {
      console.error("Erreur lors de la transformation en client :", error);
      showToast("Une erreur est survenue lors de la transformation.", "error");
    }
  };    

  return (
    <div className="bg-white shadow-md rounded-lg p-6 h-[300px] w-[400px] flex flex-col">
      <div className="flex flex-col items-center justify-center pb-4">
        <h2 className="text-lg font-semibold mb-2">À Faire</h2>

        {prospect ? (
          prochainesActions.length > 0 ? (
            <div className="space-y-3 w-full">
              <ul>
                {prochainesActions.slice(0, 2).map((action, index) => (
                  <li
                    key={index}
                    className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">{action.icon}</div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-700">
                          {action.titre}
                          {action.date && ` – ${action.date}`}
                        </p>
                        <p className="text-sm text-gray-600 italic">
                          ({action.type}{action.canal !== "N/A" && ` via ${action.canal}`})
                        </p>
                      </div>
                    </div>
                    <button
                      className="flex items-center justify-center gap-2 self-end text-sm text-green-600 hover:text-green-800"
                      onClick={() => handleMarkAsDone(action)}
                    >
                      <CheckIcon className="w-4 h-4" />
                      Marquer comme faite
                    </button>
                  </li>
                ))}
              </ul>
              {showAuditButton && (
              <div className="mt-4 flex justify-center">
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  onClick={() => setShowConfirmModal(true)}
                >
                  Audit Patrimonial
                </button>
              </div>
            )}  
            </div>          

          ) : (
            <p className="text-sm text-gray-500">Aucune action planifiée</p>
          )
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 text-center min-h-[220px]">
            <img src={logo} alt="Logo Nanthiat" className="w-20 h-20 mb-4" />
            <p className="text-gray-500">
              Sélectionnez un prospect pour voir les prochaines actions.
            </p>
          </div>
        )}
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[350px]">
            <h3 className="text-lg font-semibold mb-4">Confirmer la transformation en client ?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Cette action transférera les données dans les familles, mettra à jour le statut et supprimera le prospect.
            </p>
            <div className="flex justify-between">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                onClick={handleConfirmAudit}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
      {showFinalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[350px] text-center">
            <h3 className="text-lg font-semibold mb-4">Transformation réussie ✅</h3>
            <p className="text-sm text-gray-600 mb-6">
              Le prospect a été converti en client. Vous pouvez désormais consulter sa fiche famille.
            </p>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => navigate("/prospects/familles")}
            >
              Aller à la fiche famille
            </button>
          </div>
        </div>
      )}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
          >
            <div className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${notification.type === "success" ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
              <div className="shrink-0">
                {notification.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-gray-700">
                <p className="font-semibold">{notification.type === "success" ? "Succès" : "Erreur"}</p>
                <p className="text-sm">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
